import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { usePortalGet } from './PortalOrdersPage';

// Every shipment on the Customer's orders (/portal/<name>/shipments),
// newest first: ship date, carrier, the order it went out on and that
// order's type, tracking number and Track parcel where the carrier has a
// tracking page. Each shipment opens to list its packages (issues #300,
// #313): name, contents, weight and dimensions, and the packages inside
// each.

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };
const MUTED = { fontSize: 14, color: '#5a6b85' };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' };
const TH = { padding: '8px 10px 8px 0', fontSize: 12, fontWeight: 600, color: '#5a6b85', textAlign: 'left', whiteSpace: 'nowrap' };
const TD = { padding: '10px 10px 10px 0', fontSize: 14, verticalAlign: 'top' };
const COLUMNS = 8;

// Every package, the ones inside others included.
const countPackages = (packages) => packages.reduce((n, p) => n + 1 + countPackages(p.packages || []), 0);

function packagesSummary(packages) {
  if (!packages.length) return '—';
  const count = countPackages(packages);
  if (count === 1) return packages[0].label;
  return `${count} packages`;
}

// One package: its name, weight and dimensions, then its own contents and
// the packages inside it.
function PackageDetail({ pkg, nested }) {
  const details = [pkg.weight, pkg.dimensions].filter(Boolean).join(' · ');
  return (
    <div style={nested ? { marginTop: 8, paddingTop: 8, borderTop: '1px solid #e3e7ee', marginLeft: 12 } : { background: '#f6f8fa', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{pkg.label}</div>
      {details && <div style={{ fontSize: 13, color: '#5a6b85' }}>{details}</div>}
      {pkg.contents.map((c, i) => (
        <div key={`${c.sku}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, marginTop: 2 }}>
          <span><span style={{ fontWeight: 600 }}>{c.sku}</span> <span style={{ color: '#5a6b85' }}>{c.name}</span></span>
          <span style={{ whiteSpace: 'nowrap' }}>{c.qty}{c.unit ? ` ${c.unit}` : ''}</span>
        </div>
      ))}
      {(pkg.packages || []).map((inner) => <PackageDetail key={inner.id} pkg={inner} nested />)}
    </div>
  );
}

function PortalShipmentsPage({ portalName, onNavigate, onSessionEnded }) {
  const { data: shipments, error } = usePortalGet(portalName, '/shipments', null, onSessionEnded, 'Could not load your shipments');
  const [openId, setOpenId] = useState(null);

  return (
    <div style={CARD_STYLE}>
      <h1 style={{ margin: '0 0 14px', fontSize: 20, fontWeight: 600 }}>Shipments</h1>
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && shipments === null && <div style={MUTED}>Loading...</div>}
      {shipments && shipments.length === 0 && <div style={MUTED}>Nothing has shipped yet.</div>}
      {shipments && shipments.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={TH}>Ship date</th>
                <th style={TH}>Carrier</th>
                <th style={TH}>Order type</th>
                <th style={TH}>Order</th>
                <th style={TH}>PO reference</th>
                <th style={TH}>Tracking number</th>
                <th style={TH}>Packages</th>
                <th style={TH} aria-label="Track" />
              </tr>
            </thead>
            <tbody>
              {shipments.map((s) => (
                <React.Fragment key={s.id}>
                <tr style={{ borderBottom: openId === s.id ? 'none' : '1px solid #eef1f5' }}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{s.shipped_date || '—'}</td>
                  <td style={TD}>{s.carrier || '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{s.order_type_label || '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => onNavigate(`orders/${s.order_id}`)} title={`Open ${s.display_number}`} style={LINK_BUTTON}>
                      {s.display_number}
                    </button>
                  </td>
                  <td style={TD}>{s.po_reference || '—'}</td>
                  <td style={{ ...TD, wordBreak: 'break-all' }}>{s.tracking_number || '—'}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    {s.packages.length > 0 ? (
                      <button type="button" onClick={() => setOpenId(openId === s.id ? null : s.id)} aria-expanded={openId === s.id} style={LINK_BUTTON}>
                        {packagesSummary(s.packages)} {openId === s.id ? '▴' : '▾'}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {s.tracking_url && (
                      <a
                        href={s.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Track this shipment on ${s.carrier}'s website`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#1976d2', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                      >
                        Track parcel <ExternalLink size={13} />
                      </a>
                    )}
                  </td>
                </tr>
                {openId === s.id && (
                  <tr style={{ borderBottom: '1px solid #eef1f5' }}>
                    <td colSpan={COLUMNS} style={{ padding: '0 0 10px' }}>
                      {s.packages.map((p) => <PackageDetail key={p.id} pkg={p} />)}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PortalShipmentsPage;
